<template>
  <div v-if="authenticated">
    <b-form @submit="onSubmit">
      <KTPortlet :title="title">
        <template v-slot:body>
          <div class="d-flex justify-content-center">
            <b-spinner
              v-if="loading"
              variant="primary"
              label="Spinning"
            ></b-spinner>
          </div>
          <b-row cols="1" v-if="formProperties && formProperties.length">
            <b-col
              v-for="(category, index) in permissionCategories"
              :key="index"
            >
              <b-form-group label-size="lg" label-align="center">
                <template v-slot:label>
                  <h4>{{ category.name }}</h4>
                </template>
                <FormGenerator
                  :formProperties="category.permissions"
                  v-model="newPermissions"
                  class="d-flex justify-content-center"
                  cols="1"
                  cols-sm="5"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </template>
        <template v-slot:foot>
          <b-row class="d-flex justify-content-between">
            <b-button type="submit" variant="primary">Save</b-button>
            <b-button type="button" @click="cancel" variant="secondary"
              >Cancel</b-button
            >
          </b-row>
        </template>
      </KTPortlet>
    </b-form>
    <LoadingModal v-bind="loadingModal" />
    <AlertModal
      v-bind="alertModal"
      v-model="alertModal.show"
      @dismiss="resetAlert"
    />
  </div>
</template>

<script>
import KTPortlet from "@/views/partials/content/Portlet";
import FormGenerator from "@/components/form-elements/generators/FormGenerator.vue";
import AlertModal from "@/components/widgets/AlertModal.vue";
import LoadingModal from "@/components/widgets/LoadingModal.vue";
import { API, Auth } from "aws-amplify";
import Format from "@/services/Formatting.js";

const checkboxTemplate = {
  fieldName: "",
  label: "",
  checkedValue: "",
  uncheckedValue: "",
  checked: false,
  fieldType: "checkbox",
  category: "OTHER",
  size: "lg"
};

export default {
  components: {
    KTPortlet,
    FormGenerator,
    AlertModal,
    LoadingModal
  },
  props: {
    agent: {
      type: Object
    }
  },
  data() {
    return {
      agentPermissions: [],
      newPermissions: {},
      formProperties: [],
      loadingModal: {
        dialog: false,
        text: ""
      },
      groupsLimit: 60,
      alertModal: {
        show: false,
        text: "",
        type: AlertModal.INFORMATION,
        callback: null
      }
    };
  },
  computed: {
    title() {
      return `Set Permissions For ${this.agentName}`;
    },
    agentName() {
      if (!!this.agent) {
        return !!this.agent.name ? this.agent.name : this.agent.email;
      }
      return "Agent";
    },
    loading() {
      return !this.formProperties || !this.formProperties.length;
    },
    authenticated() {
      return this.$role.verify([
        this.$role.EDIT_USER_PERMISSIONS,
        this.$role.MERCHANT_ADMIN
      ]);
    },
    permissionCategories() {
      return this.$role.PERMISSION_CATEGORIES.reduce((arr, category) => {
        const name = category;
        const permissions = this.filterPermissionsByCategory(category);
        if (permissions.length) arr.push({ name, permissions });
        return arr;
      }, []);
    }
  },
  methods: {
    async getPermissions() {
      let payload = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        }
      };

      const permissions = await API.get(
        "AdminQueries",
        `/listPermissions?limit=${this.groupsLimit}`,
        payload
      );

      return permissions.groups.map(g => g.GroupName);
    },
    async getAgentPermissions() {
      let payload = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        queryStringParameters: {
          username: this.agent.username
        }
      };

      const agentPermissions = await API.get(
        "AdminQueries",
        "/listPermissionsForUser",
        payload
      );

      return agentPermissions.map(g => g.GroupName);
    },
    filterPermissionsByCategory(category) {
      return this.formProperties.filter(
        permission => permission.category === category
      );
    },
    getPermissionObjectByName(permissionName) {
      let permission = this.$role.PERMISSIONS[permissionName];
      if (!permission)
        permission = {
          displayName: Format.transformValueToLabel(permissionName),
          category: this.$role.OTHER
        };

      return permission;
    },
    generatePermissionsCheckboxList(permission) {
      const permissionName = permission;
      const permissionObject = this.getPermissionObjectByName(permissionName);
      const permissionLabel = permissionObject.displayName;
      const userHasPermission = this.agentPermissions.includes(permissionName);
      const property = { ...checkboxTemplate };
      property.fieldName = permissionName;
      property.label = permissionLabel;
      property.checkedValue = true;
      property.uncheckedValue = false;
      property.checked = userHasPermission;
      property.category = permissionObject.category;
      return property;
    },
    getPermissionsList: async function() {
      const [allPermissions, agentPermissions] = await Promise.all([
        this.getPermissions(),
        this.getAgentPermissions()
      ]);
      this.agentPermissions = agentPermissions;
      return allPermissions.map(this.generatePermissionsCheckboxList);
    },
    onSubmit(e) {
      e.preventDefault();
      this.startLoadingDialog(`Saving Permissions`);
      this.updateAgentPermissions(this.newPermissions)
        .then(failedChanges => {
          if (failedChanges === "")
            this.stopLoadingDialog(
              `Permissions for ${this.agentName} have been saved`,
              AlertModal.SUCCESS,
              this.goBack
            );
          else
            this.stopLoadingDialog(
              `There was an error changing the following permissions for ${this.agentName}: ${failedChanges}`,
              AlertModal.ERROR
            );
        })
        .catch(e => {
          this.$logger.error(e);
          this.stopLoadingDialog(
            `There was an error modifying permissions for ${this.agentName}.`,
            AlertModal.ERROR
          );
        });
    },
    cancel() {
      this.goBack();
    },
    goBack() {
      this.$emit("back");
    },
    generatePermissionsModificationPayload(oldPermissions, newPermissions) {
      const modifiedPermissions = [];
      for (let key in newPermissions) {
        if (newPermissions.hasOwnProperty(key)) {
          if (newPermissions[key]) {
            if (!oldPermissions.includes(key))
              modifiedPermissions.push({ name: key, add: true });
          } else {
            if (oldPermissions.includes(key))
              modifiedPermissions.push({ name: key, add: false });
          }
        }
      }
      return modifiedPermissions;
    },
    async updateAgentPermissions(newPermissions) {
      const permissions = this.generatePermissionsModificationPayload(
        this.agentPermissions,
        newPermissions
      );

      let payload = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `${(await Auth.currentSession())
            .getIdToken()
            .getJwtToken()}`
        },
        body: {
          username: this.agent.username,
          permissions
        }
      };

      const result = await API.post(
        "AdminQueries",
        "/modifyUserPermissions",
        payload
      );

      const failedChanges = result.filter(permissions => !permissions.success);
      return failedChanges.map(c => c.name).join(", ");
    },
    alert(text, type, callback) {
      if (text) this.alertModal.text = text;
      if (type) this.alertModal.type = type;
      if (!!callback) this.alertModal.callback = callback;
      if (this.alertModal.text) this.alertModal.show = true;
    },
    resetAlert() {
      this.alertModal.text = "";
      this.alertModal.type = AlertModal.INFORMATION;
      if (!!this.alertModal.callback) {
        const callback = this.alertModal.callback;
        this.alertModal.callback = null;
        callback();
      }
    },
    startLoadingDialog(text) {
      this.loadingModal.text = text;
      this.loadingModal.dialog = true;
    },
    stopLoadingDialog(alertText, alertType, callback) {
      this.loadingModal.dialog = false;
      this.loadingModal.text = "";
      this.alert(alertText, alertType, callback);
    }
  },
  created: async function() {
    this.getPermissionsList()
      .then(response => {
        this.formProperties = response;
      })
      .catch(error => {
        this.$logger.error(error);
        this.alert(
          `Unable to retrieve permissions for ${this.agentName}.`,
          AlertModal.ERROR
        );
      });
  }
};
</script>

<style lang="scss" scoped></style>
